// Sidebar navigation component using Ahua NavigationRail
import { SPACE_SIDEBAR_WIDTH } from '@/utils/constants';
import { NavigationRail, NavigationItem } from '@yaradigitallabs/ahua-react';
import { Suspense } from 'react';
import { useNavigate } from 'react-router-dom';

// Props for Sidebar component
// routes: array of navigation items
// collapsed: sidebar collapsed state
// setCollapsed: function to update collapsed state
// selected: currently selected route url

type SidebarProps = {
  routes: Array<{
    id: string;
    text: string;
    icon: string;
    selected: boolean;
    url: string;
    disabled?: boolean;
  }>;
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
  selected: string;
};

// Sidebar component renders navigation rail with routes and utility items
const Sidebar = ({
  routes,
  collapsed,
  setCollapsed,
  selected,
}: SidebarProps) => {
  const navigate = useNavigate();

  return (
    // Suspense for lazy loading fallback
    <Suspense fallback={<div>Loading...</div>}>
      <div className="flex min-h-[calc(100vh-60px)]">
        {/* NavigationRail from Ahua library */}
        <NavigationRail
          onChange={value => setCollapsed(value)}
          defaultCollapsed={collapsed}
          style={!collapsed ? { width: `${SPACE_SIDEBAR_WIDTH.OPEN}px` } : {}}
        >
          {/* Main navigation items */}
          <NavigationRail.Middle className="gap-3">
            {routes.map(item => {
              return (
                <NavigationRail.Item
                  key={item.id}
                  disabled={item.disabled}
                  className={[
                    collapsed ? 'p-0!' : '',
                    item.disabled ? 'cursor-not-allowed!' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => {
                    if (!item.disabled) {
                      navigate(item.url);
                    }
                  }}
                  selected={selected === item.url}
                >
                  <NavigationItem.Head>
                    <NavigationItem.Icon icon={item.icon} />
                  </NavigationItem.Head>
                  {/* Show text only if not collapsed */}
                  {!collapsed && (
                    <NavigationItem.Body
                      className={[
                        'whitespace-nowrap',
                        item.disabled ? 'cursor-not-allowed!' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      {item.text}
                    </NavigationItem.Body>
                  )}
                </NavigationRail.Item>
              );
            })}
          </NavigationRail.Middle>
          {/* Separator and utility items at the end */}
          <NavigationRail.Separator />
          <NavigationRail.End>
            <NavigationRail.CollapseItem />
            <NavigationRail.Item>
              <NavigationItem.Head>
                <NavigationItem.Icon icon="Settings" />
              </NavigationItem.Head>
              {!collapsed && (
                <NavigationItem.Body size={'s'}>Settings</NavigationItem.Body>
              )}
            </NavigationRail.Item>

            <NavigationRail.Item>
              <NavigationItem.Head>
                <NavigationItem.Icon icon="Privacy" />
              </NavigationItem.Head>
              {!collapsed && (
                <NavigationItem.Body size={'s'}>
                  Privacy Notice
                </NavigationItem.Body>
              )}
            </NavigationRail.Item>
          </NavigationRail.End>
        </NavigationRail>
      </div>
    </Suspense>
  );
};
export default Sidebar;
